import React from 'react';
import styled from 'styled-components';
import { useProjectInfoListStore } from '../../shared/store/projectInfoStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { PIInput } from '../../shared/component/headless/input/InputBody';
import { ProjectInfoData } from '../../shared/dto/EntityRepository';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { useProjectInfoActions } from '../../shared/hooks/useApi/useProjectInfo';
import { PROJECT_INFO_NAME_MAX_LENGTH } from '../../shared/constants/limits';
import SelectBox from '../../shared/component/SelectBox';
import { ModalType, ProjectInfoType, SelectType } from '../../shared/enum/EnumRepository';
import { getProjectInfoNamePlaceholder, getProjectInfoTypeLabel } from '../../shared/util/getProjectInfoTypeLabel';
import ProjectInfoValues from './ProjectInfoValues';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { useModalStore } from '../../shared/store/portal/modalStore';
import reorderIcon from '../../asset/icon/reorder.png';
import {
  getProjectInfoDisplayName,
  normalizeProjectInfoType,
  withProjectInfoType,
} from '../../shared/util/projectInfo';

interface ProjectInfoProps {
  data: ProjectInfoData;
}

const ProjectInfo: React.FC<ProjectInfoProps> = ({ data }) => {
  const { isEditMode } = useEditMode();
  const handleChange = useProjectInfoListStore((s) => s.updateProjectInfo);
  const { deleteProjectInfo } = useProjectInfoActions();
  const setStandardModal = useModalStore((s) => s.setStandardModal);
  const projectInfoType = normalizeProjectInfoType(data.type);

  const handleDelete = async () => {
    try {
      await deleteProjectInfo({ id: data.id });
    } catch (err) {
    }
  }

  const handleTypeChange = (type: ProjectInfoType) => {
    const nextType = normalizeProjectInfoType(type);
    if (nextType === projectInfoType) return;
    handleChange(data.id, withProjectInfoType(nextType));
  };

  const handleReorderValues = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: 'Info Value',
      value: data.id,
      handleChange: () => {},
    });
  };

  return (
    <ProjectInfoItem
      data-has-changed={data.hasChanged ? "true" : "false"}    // 편집 모드 진입 시 변경사항이 있으면 스크롤
    >
      <ProjectInfoBody>
        {isEditMode &&
          <TypeSelectRow>
            <SelectBoxWrapper>
              <SelectBox
                value={projectInfoType}
                selectType={SelectType.PROJECT_INFO_TYPE}
                handleChange={handleTypeChange}
                direction={false}
                getDisplayText={getProjectInfoTypeLabel}
              />
            </SelectBoxWrapper>
            {data.customValues.length > 1 && (
              <UniversalIconBtn
                size="small"
                border={false}
                isTransparent
                backgroundImg={reorderIcon}
                onClick={handleReorderValues}
                aria-label="Reorder info values"
              />
            )}
          </TypeSelectRow>
        }
        <ProjectInfoContentComp>
          <LabelWrapper>
            <MoleculeInputDiv
              value={getProjectInfoDisplayName(data.customName)}
              placeholder={getProjectInfoNamePlaceholder(projectInfoType)}
              handleChange={(e) => handleChange(data.id, { customName: e.target.value })}
              inputStyle={PIInput}
              StyledDiv={NameSection}
              maxLength={PROJECT_INFO_NAME_MAX_LENGTH}
            />
          </LabelWrapper>
          <ProjectInfoValues data={data} onDelete={handleDelete} />
        </ProjectInfoContentComp>
      </ProjectInfoBody>
    </ProjectInfoItem>
  );
}

const ProjectInfoItem = styled.div`
  position: relative;
  display: flex;
  height: fit-content;
  gap: clamp(12px, 1vw, 14px);
  margin-bottom: clamp(6px, 1.5vw, 2rem);
`;

const ProjectInfoBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 4px;
`;

const TypeSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2vw;
`;

const SelectBoxWrapper = styled.article`
  width: clamp(70px, 15vw, 220px);
  width: clamp(70px, 23vw, 90px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const ProjectInfoContentComp = styled.div`
  display: flex;
  align-items: flex-start;
  gap: clamp(12px, 4vw, 56px);
  flex: 1;
  min-width: 0;
`;

const LabelWrapper = styled.div`
  width: clamp(70px, 15vw, 220px);
  display: flex;
`;

const NameSection = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
`;

export default React.memo(ProjectInfo);
